import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  TextField,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import { tokens } from "../../theme";

const QuizPage = () => {
  const { id: courseId } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);

  // delete states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);

  // add states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [confirmAddOpen, setConfirmAddOpen] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    question: "",
    answer1: "",
    answer2: "",
    answer3: "",
    answer4: "",
    correctAnswer: 1,
  });

  // edit states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [confirmEditOpen, setConfirmEditOpen] = useState(false);
  const [editQuestion, setEditQuestion] = useState(null);

  /** ---------------- LOAD QUIZZES ---------------- */
  useEffect(() => {
    setLoading(true);
    axiosInstance
      .get(`/courses/${courseId}/quiz-questions`)
      .then((res) => setQuestions(res.data))
      .catch(() => toast.error("Failed to load quiz questions"))
      .finally(() => setLoading(false));
  }, [courseId]);

  /** ---------------- DELETE ---------------- */
  const handleDeleteClick = (questionId) => {
    setQuestionToDelete(questionId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setLoading(true);
    axiosInstance
      .delete(`/courses/${courseId}/quiz-questions/${questionToDelete}`)
      .then(() => {
        toast.success("Question deleted successfully");
        setQuestions((prev) => prev.filter((q) => q.id !== questionToDelete));
      })
      .catch(() => toast.error("Failed to delete question"))
      .finally(() => {
        setLoading(false);
        setDeleteDialogOpen(false);
        setQuestionToDelete(null);
      });
  };

  /** ---------------- ADD ---------------- */
  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setNewQuestion((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddConfirm = () => setConfirmAddOpen(true);

  const handleAddSubmit = () => {
    setLoading(true);
    axiosInstance
      .post(`/courses/${courseId}/quiz-questions`, newQuestion)
      .then((res) => {
        toast.success("Quiz added successfully");
        setQuestions((prev) => [...prev, res.data]);
        setAddDialogOpen(false);
        setNewQuestion({
          question: "",
          answer1: "",
          answer2: "",
          answer3: "",
          answer4: "",
          correctAnswer: 1,
        });
      })
      .catch(() => toast.error("Failed to add quiz"))
      .finally(() => {
        setLoading(false);
        setConfirmAddOpen(false);
      });
  };

  /** ---------------- EDIT ---------------- */
  const handleEditClick = (id) => {
    const q = questions.find((item) => item.id === id);
    if (q) {
      setEditQuestion({ ...q }); // make a copy
      setEditDialogOpen(true);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditQuestion((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditConfirm = () => setConfirmEditOpen(true);

  const handleEditSubmit = () => {
    setLoading(true);
    axiosInstance
      .put(
        `/courses/${courseId}/quiz-questions/${editQuestion.id}`,
        editQuestion,
      )
      .then((res) => {
        toast.success("Quiz updated successfully");
        setQuestions((prev) =>
          prev.map((q) => (q.id === editQuestion.id ? res.data : q)),
        );
        setEditDialogOpen(false);
        setEditQuestion(null);

        // 🔄 Refresh page after edit
        window.location.reload();
      })
      .catch(() => toast.error("Failed to update quiz"))
      .finally(() => {
        setLoading(false);
        setConfirmEditOpen(false);
      });
  };

  return (
    <Box m="20px">
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h4" color={colors.blueAccent[500]}>
          Quiz
        </Typography>
        <Button
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: colors.greenAccent[500],
            color: colors.grey[900],
          }}
          onClick={() => setAddDialogOpen(true)}
        >
          {loading ? <CircularProgress size={20} /> : "Add Quiz"}
        </Button>
      </Box>

      {/* Quiz List */}
      {questions.map((q) => (
        <Paper
          key={q.id}
          sx={{ p: 2, mb: 2, backgroundColor: colors.primary[400] }}
        >
          <Typography variant="h6" color={colors.greenAccent[400]}>
            {q.question}
          </Typography>
          <RadioGroup value={q.correctAnswer}>
            {[1, 2, 3, 4].map((num) => (
              <FormControlLabel
                key={num}
                value={num}
                control={<Radio />}
                label={q[`answer${num}`]}
                disabled
                sx={{
                  "& .MuiSvgIcon-root": {
                    color:
                      q.correctAnswer === num
                        ? colors.greenAccent[500]
                        : colors.grey[100],
                  },
                }}
              />
            ))}
          </RadioGroup>
          <Box mt={1} display="flex" gap={1}>
            <Button
              variant="outlined"
              disabled={loading}
              sx={{
                borderColor: colors.blueAccent[500],
                color: colors.blueAccent[500],
              }}
              onClick={() => handleEditClick(q.id)}
            >
              {loading ? <CircularProgress size={16} /> : "Edit"}
            </Button>
            <Button
              variant="outlined"
              disabled={loading}
              sx={{
                borderColor: colors.redAccent[500],
                color: colors.redAccent[500],
              }}
              onClick={() => handleDeleteClick(q.id)}
            >
              {loading ? <CircularProgress size={16} /> : "Delete"}
            </Button>
          </Box>
        </Paper>
      ))}

      {/* Delete Confirmation */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          Are you sure you want to delete this question?
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            sx={{ color: colors.redAccent[500] }}
            onClick={handleDeleteConfirm}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Quiz Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
        <DialogTitle>Add New Quiz</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
        >
          <TextField
            label="Question"
            name="question"
            value={newQuestion.question}
            onChange={handleAddChange}
            fullWidth
            multiline
            rows={3}
          />
          <TextField
            label="Answer 1"
            name="answer1"
            value={newQuestion.answer1}
            onChange={handleAddChange}
            fullWidth
          />
          <TextField
            label="Answer 2"
            name="answer2"
            value={newQuestion.answer2}
            onChange={handleAddChange}
            fullWidth
          />
          <TextField
            label="Answer 3"
            name="answer3"
            value={newQuestion.answer3}
            onChange={handleAddChange}
            fullWidth
          />
          <TextField
            label="Answer 4"
            name="answer4"
            value={newQuestion.answer4}
            onChange={handleAddChange}
            fullWidth
          />
          <TextField
            label="Correct Answer (1-4)"
            name="correctAnswer"
            type="number"
            inputProps={{ min: 1, max: 4 }}
            value={newQuestion.correctAnswer}
            onChange={handleAddChange}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ color: colors.redAccent[500] }}
            onClick={() => setAddDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            sx={{
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[900],
            }}
            onClick={handleAddConfirm}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Confirmation */}
      <Dialog open={confirmAddOpen} onClose={() => setConfirmAddOpen(false)}>
        <DialogTitle>Are you sure you want to add this quiz?</DialogTitle>
        <DialogActions>
          <Button
            sx={{ color: colors.redAccent[500] }}
            onClick={() => setConfirmAddOpen(false)}
          >
            Cancel
          </Button>
          <Button
            sx={{
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[900],
            }}
            onClick={handleAddSubmit}
          >
            Yes, Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Quiz Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Quiz</DialogTitle>
        {editQuestion && (
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
          >
            <TextField
              label="Question"
              name="question"
              value={editQuestion.question}
              onChange={handleEditChange}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Answer 1"
              name="answer1"
              value={editQuestion.answer1}
              onChange={handleEditChange}
              fullWidth
            />
            <TextField
              label="Answer 2"
              name="answer2"
              value={editQuestion.answer2}
              onChange={handleEditChange}
              fullWidth
            />
            <TextField
              label="Answer 3"
              name="answer3"
              value={editQuestion.answer3}
              onChange={handleEditChange}
              fullWidth
            />
            <TextField
              label="Answer 4"
              name="answer4"
              value={editQuestion.answer4}
              onChange={handleEditChange}
              fullWidth
            />
            <TextField
              label="Correct Answer (1-4)"
              name="correctAnswer"
              type="number"
              inputProps={{ min: 1, max: 4 }}
              value={editQuestion.correctAnswer}
              onChange={handleEditChange}
              fullWidth
            />
          </DialogContent>
        )}
        <DialogActions>
          <Button
            sx={{ color: colors.redAccent[500] }}
            onClick={() => setEditDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            sx={{
              backgroundColor: colors.blueAccent[500],
              color: colors.grey[900],
            }}
            onClick={handleEditConfirm}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Confirmation */}
      <Dialog open={confirmEditOpen} onClose={() => setConfirmEditOpen(false)}>
        <DialogTitle>
          Are you sure you want to save changes to this quiz?
        </DialogTitle>
        <DialogActions>
          <Button
            sx={{ color: colors.redAccent[500] }}
            onClick={() => setConfirmEditOpen(false)}
          >
            Cancel
          </Button>
          <Button
            sx={{
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[900],
            }}
            onClick={handleEditSubmit}
          >
            Yes, Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuizPage;
